// built-from: /chat-component v3 impl:chat-component role:implementation
// Pure helpers extracted from useSession. No React, no module-level
// state — safe to call from any context. Tested by chat2Stream.test.ts.

import type { SpineStep, DoneModel, ChatActivity } from '../hooks/sessionTypes';
import { coerceNewModel } from './sessionCoercers';

// `DoneModel` is defined in hooks/sessionTypes (alongside the SpineStep /
// SessionPhase / NewModel types it composes) so the type layer carries no
// edge back into this transport module — breaking the chat2Stream ↔
// sessionTypes / sessionCoercers cycle (no-circular). It is re-exported here
// (type-only) so existing consumers (useVoice, chat2Stream.test) that import
// it from '../utils/chat2Stream' keep working unchanged.
export type { DoneModel } from '../hooks/sessionTypes';

// Stream one /api/chat2 turn. Shared by text (handleTextMessage) and
// voice (voice-transcript path). Invokes the per-event callbacks as
// frames arrive; resolves when `done` fires or rejects on `error` /
// transport failure / no terminal event. Callers decide what to do
// with the NQ and newModel — voice ignores NQ because the Realtime
// API is already speaking a reply, while text renders it early.
// ── Activity feedback (056; owner-agreed activity feedback, 2026-07-09) ──
// The wire shape of one `activity` SSE frame: the server's advisory tool
// loop reports started/completed/failed around each background run (e.g.
// research on a vision turn), and the frame flushes DURING the turn, before
// the done frame. Lifted with the same allowlist discipline as factEvents:
// known fields only, unknown fields dropped, malformed frames ignored.
export type ChatActivityEvent = {
  id: string;
  kind?: string;
  label: string;
  status: 'started' | 'completed' | 'failed';
  detail?: string;
};

export type Chat2StreamHandlers = {
  // The early `nextQuestion` event forwards the turn `kind` as a 2nd arg
  // so the voice speak-decision can gate on it (003-narrate-voice-feedback,
  // FR-009): the server carries `kind` for nudge/offer in narrate and
  // OMITS it for narrate-question (so the 2nd arg is undefined there). The
  // text path ignores the 2nd arg — its observable behaviour is unchanged.
  onNextQuestion?: (text: string, kind?: 'question' | 'nudge' | 'offer') => void;
  // 056: the per-frame activity report (see ChatActivityEvent). Optional and
  // additive; a caller without it consumes the stream exactly as today.
  onActivity?: (activity: ChatActivityEvent) => void;
  onDone: (agentResponse: string, newModel: DoneModel) => void;
};

export type FacilitationInputMeta = {
  inputId: string;
  modality: 'text' | 'voice' | 'attachment' | 'system';
  sourceRef?: string;
};

// Fold one wire activity event into the view-state list both chat hooks
// useChat holds for the in-flight turn. Pure and shared,
// so the wire-to-view mapping exists exactly once: started appends a running
// entry, completed settles it (carrying the optional detail), failed marks
// it failed (the panel renders nothing for a failed entry). Upserts by id so
// a missed started frame still settles cleanly.
export function applyActivityEvent(
  prev: ChatActivity[],
  event: ChatActivityEvent,
): ChatActivity[] {
  const status =
    event.status === 'started' ? 'running' : event.status === 'completed' ? 'complete' : 'failed';
  const item: ChatActivity = { id: event.id, label: event.label, status };
  if (event.kind !== undefined) item.kind = event.kind;
  if (event.detail !== undefined) item.detail = event.detail;
  const existing = prev.findIndex((a) => a.id === event.id);
  if (existing === -1) return [...prev, item];
  const next = prev.slice();
  next[existing] = { ...prev[existing], ...item };
  return next;
}

export async function streamChat2(
  sessionId: string,
  message: string,
  handlers: Chat2StreamHandlers,
  files: File[] = [],
  signal?: AbortSignal,
  // 041 (F6/F7): an optional chat context discriminator ('vision') threaded
  // into the request so the server selects the vision context (FR-017). When
  // ABSENT the request shape is BYTE-IDENTICAL to today's discovery turn — no
  // `context` field is added to the JSON body or the FormData (pinned by
  // chat2Stream.test.ts, mirroring the engine-side chat2ContextDefault guard).
  context?: string,
  // 049 (FR-001): an optional on-screen `screenContext` snapshot (a
  // ScreenChatContext) threaded into the turn so the engine reads what the user
  // is looking at / selecting. When PRESENT it is serialised onto the request:
  // `screenContext` in the JSON body, and a JSON-stringified `screenContext`
  // FormData entry. When ABSENT the request is BYTE-IDENTICAL to today (no
  // `screenContext` field is added) — pinned by chat2Stream.test.ts.
  screenContext?: unknown,
  // 049 (FR-017): the candidates the user accepted in the review readout. When
  // PRESENT (non-empty) it is serialised onto the request (`acceptedCandidates`
  // in the JSON body, JSON-stringified in the FormData) so the engine writes each
  // accepted value via the plan slot's capability. When ABSENT the request is
  // BYTE-IDENTICAL to today (no `acceptedCandidates` field is added).
  acceptedCandidates?: unknown,
  inputMeta?: FacilitationInputMeta,
): Promise<void> {
  const hasAccepted =
    Array.isArray(acceptedCandidates) && acceptedCandidates.length > 0;
  // Switch to multipart when attachments are present. The server content-
  // negotiates via Content-Type, so pure-text turns keep their JSON path
  // (no shape change for voice or text-only flows).
  let requestInit: RequestInit;
  if (files.length > 0) {
    const form = new FormData();
    form.append('sessionId', sessionId);
    form.append('message', message);
    // Only append the context discriminator when present — a discovery turn's
    // FormData is unchanged (no `context` entry).
    if (context) form.append('context', context);
    // Only append the on-screen snapshot when present (JSON-stringified) — a turn
    // without one keeps its FormData byte-identical (no `screenContext` entry).
    if (screenContext !== undefined && screenContext !== null) {
      form.append('screenContext', JSON.stringify(screenContext));
    }
    if (hasAccepted) form.append('acceptedCandidates', JSON.stringify(acceptedCandidates));
    if (inputMeta) form.append('inputMeta', JSON.stringify(inputMeta));
    for (const f of files) form.append('files', f, f.name);
    // NB: do not set Content-Type manually — fetch sets the correct
    // multipart boundary when body is a FormData.
    requestInit = {
      method: 'POST',
      headers: { Accept: 'text/event-stream' },
      body: form,
      signal,
    };
  } else {
    // Only include `context` / `screenContext` in the JSON body when present — a
    // discovery turn's body is exactly { sessionId, message } (shape unchanged).
    const jsonBody: Record<string, unknown> = { sessionId, message };
    if (context) jsonBody.context = context;
    if (screenContext !== undefined && screenContext !== null) {
      jsonBody.screenContext = screenContext;
    }
    if (hasAccepted) jsonBody.acceptedCandidates = acceptedCandidates;
    if (inputMeta) jsonBody.inputMeta = inputMeta;
    requestInit = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(jsonBody),
      signal,
    };
  }

  const response = await fetch('/api/chat2', requestInit);

  if (!response.ok || !response.body) {
    const errJson = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(errJson.error || `HTTP ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let sawDone = false;

  try {
    while (true) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      const { value, done: streamDone } = await reader.read();
      if (streamDone) break;
      buffer += decoder.decode(value, { stream: true });

      let sepIdx;
      while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);

        let eventName = 'message';
        let dataStr = '';
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) eventName = line.slice(6).trim();
          else if (line.startsWith('data:')) dataStr += line.slice(5).replace(/^\s/, '');
        }
        if (!dataStr) continue;

        let data;
        try { data = JSON.parse(dataStr); } catch { continue; }

        if (eventName === 'nextQuestion') {
          // Forward the early-event `kind` (nudge/offer in narrate; absent
          // for narrate-question ⇒ undefined). The voice gate reads it to
          // decide whether to speak early; the text path ignores it (FR-009).
          handlers.onNextQuestion?.(data.text, data.kind);
        } else if (eventName === 'activity') {
          // 056 activity feedback (owner-agreed activity feedback,
          // 2026-07-09): lift the frame's known fields with type guards
          // (the factEvents allowlist discipline). Unknown fields are
          // dropped; a malformed frame is ignored quietly; a caller without
          // the handler consumes the stream exactly as today.
          if (
            handlers.onActivity
            && typeof data.id === 'string'
            && typeof data.label === 'string'
            && (data.status === 'started' || data.status === 'completed' || data.status === 'failed')
          ) {
            const activity: ChatActivityEvent = {
              id: data.id,
              label: data.label,
              status: data.status,
            };
            if (typeof data.kind === 'string') activity.kind = data.kind;
            if (typeof data.detail === 'string') activity.detail = data.detail;
            handlers.onActivity(activity);
          }
        } else if (eventName === 'done') {
          sawDone = true;
          // phase/spine live at the top of the done frame (siblings of
          // newModel), not inside it. Surface them on the model object
          // only when present so a pre-feature frame stays unchanged.
          const model: DoneModel = coerceNewModel(data.newModel);
          if (data.phase === 'narrate' || data.phase === 'refine') {
            model.phase = data.phase;
          }
          if (Array.isArray(data.spine)) {
            model.spine = data.spine as SpineStep[];
          }
          // The done-frame `kind` is the authoritative per-turn
          // classification (FR-008). Lift it onto the model exactly as
          // phase/spine; default to 'question' so callers never see
          // undefined for a pre-feature frame (backward-safe).
          model.kind = data.kind ?? 'question';
          // 049: lift the vision screen-aware envelope (outstanding /
          // outstanding outputs / the ≤3 inferred readout) opaquely onto the model
          // when the frame carries it, exactly as phase/spine. A discovery frame
          // omits them, so the model is unchanged for discovery (backward-safe).
          if (Array.isArray(data.outstanding)) {
            model.outstanding = data.outstanding;
          }
          if (Array.isArray(data.candidates)) {
            model.candidates = data.candidates;
          }
          // 054: lift the vision turn's appended fact events (server-ordered,
          // for the client replica) VERBATIM — an opaque copy, no per-event
          // coercion, exactly like spine. Array.isArray-guarded: a non-array
          // value is ignored, and a frame without the sibling leaves the model
          // free of the key (discovery byte-identity, T039).
          if (Array.isArray(data.factEvents)) {
            model.factEvents = data.factEvents;
          }
          // 093: the knowledge pages this turn cited, lifted onto the model the
          // same way - an allowlist entry, absent when the frame omits it, so
          // the Sources footer only appears for a turn that used the base.
          if (Array.isArray(data.citations)) {
            model.citations = (data.citations as Array<{ path?: unknown; title?: unknown }>)
              .flatMap((citation) => (
                typeof citation?.path === 'string' && citation.path
                  ? [{
                    path: citation.path,
                    title: typeof citation.title === 'string' && citation.title
                      ? citation.title
                      : citation.path,
                  }]
                  : []
              ));
          }
          if (Array.isArray(data.surfaceActions)) {
            model.surfaceActions = data.surfaceActions;
          }
          handlers.onDone(data.agentResponse, model);
          return;
        } else if (eventName === 'error') {
          throw new Error(data.message || 'Server error');
        }
      }
    }
  } finally {
    try { await reader.cancel(); } catch { /* stream already closed */ }
  }

  if (!sawDone) throw new Error('Response ended unexpectedly');
}

// Poll the code-comparison endpoint until status leaves 'pending' or
// the timeout elapses. The Service Bus worker can take a few minutes
// on a real repo; we cap at 10 min so a stuck job doesn't poll forever.
// intervalMs / timeoutMs are exposed for tests — production callers
// take the defaults (5s poll, 10min cap).
export async function pollUntilSettled(
  sessionId: string,
  refresh: () => Promise<void>,
  opts: { intervalMs?: number; timeoutMs?: number } = {},
): Promise<void> {
  const start = Date.now();
  const TIMEOUT_MS = opts.timeoutMs ?? 10 * 60 * 1000;
  const INTERVAL_MS = opts.intervalMs ?? 5000;
  while (Date.now() - start < TIMEOUT_MS) {
    try {
      const res = await fetch(`/api/sessions/${sessionId}/code-comparison`);
      if (res.ok) {
        const data = await res.json();
        if (data.status !== 'pending') {
          await refresh();
          return;
        }
      }
    } catch { /* retry next tick */ }
    await new Promise((r) => setTimeout(r, INTERVAL_MS));
  }
  await refresh();
}
